import { debounce, type DebouncedFunc } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

const BUILD_DEBOUNCE_DELAY = 300;

/**
 * Schedule a debounced computation and expose its result. The caller
 * supplies a memoized `build` callback; the hook re-runs it whenever the
 * reference changes and discards stale results in the meantime.
 *
 * Generic over the return type so the provider can swap between
 * strategies (quadtree, sorted entries, …) by branching inside the
 * callback rather than gating two parallel hooks with a flag.
 */
export function useBuildStrategy<T>(build: () => T): T | undefined {
  const [state, setState] = useState<T | undefined>(undefined);
  const debouncedRef = useRef<DebouncedFunc<(run: () => T) => void> | null>(
    null,
  );

  if (!debouncedRef.current) {
    debouncedRef.current = debounce<(run: () => T) => void>(
      (run) => setState(run()),
      BUILD_DEBOUNCE_DELAY,
    );
  }

  useEffect(() => {
    const debounced = debouncedRef.current;
    debounced?.(build);
    return () => {
      debounced?.cancel();
      setState(undefined);
    };
  }, [build]);

  return state;
}
